/**
 * Převod chybových kódů Firebase (Auth i Firestore) na české hlášky.
 *
 * Jediný slovník pro celou aplikaci – používá ho ErrorState (src/ui/AsyncState.tsx)
 * a postupně nahradí lokální mapu v AuthPage.tsx (krok 5 redesignu).
 */

import { CONTACT_EMAIL } from "./contact";

/** Chybové kódy Firebase Auth (prefix "auth/") i Firestore (bez prefixu). */
export const firebaseErrorMessages: Record<string, string> = {
  // ── Auth ──────────────────────────────────────────────────────
  "auth/email-already-in-use": "Tento e-mail je již zaregistrován.",
  "auth/invalid-email": "Neplatný formát e-mailu.",
  "auth/user-not-found": "Uživatel s tímto e-mailem neexistuje.",
  "auth/wrong-password": "Nesprávné heslo.",
  "auth/invalid-credential": "Nesprávný e-mail nebo heslo.",
  "auth/weak-password": "Heslo musí mít alespoň 6 znaků.",
  "auth/missing-password": "Zadejte prosím heslo.",
  "auth/missing-email": "Zadejte prosím e-mail.",
  "auth/user-disabled": "Tento účet je zablokovaný. Kontaktujte prosím správce.",
  "auth/too-many-requests":
    "Příliš mnoho pokusů. Zkuste to prosím za chvíli znovu.",
  "auth/network-request-failed":
    "Chyba připojení. Zkontrolujte internet a zkuste to znovu.",
  "auth/requires-recent-login":
    "Z bezpečnostních důvodů se prosím znovu přihlaste a akci opakujte.",

  // ── Vlastní kódy aplikace ─────────────────────────────────────
  "lks/sequence-out-of-range":
    "Nelze přidělit členské číslo – kontaktujte prosím správce klubu.",
  // Počítadlo členských čísel chybí (zakládá ho správce v Přípravě dat) –
  // registrace bez něj číslo nepřidělí (utils/memberNumber).
  "lks/race-gone": "Závod už neexistuje – správce ho mezitím smazal.",
  "lks/counter-missing":
    "Registraci teď nejde dokončit – kontaktujte správce klubu.",
  // Účet bez profilu i bez rozpracovaného profilu z registrace
  // (utils/completeRegistration) – AuthPage místo hlášky ukáže formulář
  // „Doplňte údaje“, resp. „Dokončit smazání účtu“ (náhrobek po smazání).
  "lks/registration-incomplete": `Registrace není dokončená – po přihlášení doplňte své údaje. Pokud to nepůjde, napište prosím správci klubu na ${CONTACT_EMAIL}.`,
  "lks/account-deleted": `Smazání účtu se nedokončilo – po přihlášení ho dokončete. Pokud jste účet smazat nechtěli, napište prosím správci klubu na ${CONTACT_EMAIL}.`,

  // ── Firestore ─────────────────────────────────────────────────
  "permission-denied": "Na tuto akci nemáte oprávnění.",
  unauthenticated: "Nejste přihlášeni. Přihlaste se prosím znovu.",
  unavailable:
    "Server je momentálně nedostupný. Zkontrolujte připojení a zkuste to znovu.",
  "deadline-exceeded": "Požadavek trval příliš dlouho. Zkuste to znovu.",
  "not-found": "Požadovaná data nebyla nalezena.",
  "resource-exhausted": "Byl překročen limit požadavků. Zkuste to později.",
  cancelled: "Požadavek byl přerušen.",
  aborted: "Operace byla přerušena souběžnou změnou. Zkuste to znovu.",
};

const DEFAULT_MESSAGE = "Něco se pokazilo. Zkuste to prosím znovu.";

/**
 * Vrátí českou hlášku pro cokoli, co přiletí do catch.
 * Rozumí chybám Firebase (podle `code`), offline stavu a obecné Error.
 */
export function firebaseErrorMessage(
  err: unknown,
  fallback: string = DEFAULT_MESSAGE
): string {
  if (err && typeof err === "object") {
    const code = (err as { code?: unknown }).code;
    if (typeof code === "string" && firebaseErrorMessages[code]) {
      return firebaseErrorMessages[code];
    }
    const message = (err as { message?: unknown }).message;
    if (typeof message === "string" && /network|offline/i.test(message)) {
      return firebaseErrorMessages["unavailable"];
    }
  }
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return firebaseErrorMessages["unavailable"];
  }
  return fallback;
}

/** True pro chyby, u kterých dává smysl tlačítko „Zkusit znovu" (síť, dostupnost). */
export function isRetryableError(err: unknown): boolean {
  if (err && typeof err === "object") {
    const code = (err as { code?: unknown }).code;
    if (typeof code === "string") {
      return [
        "unavailable",
        "deadline-exceeded",
        "cancelled",
        "aborted",
        "auth/network-request-failed",
      ].includes(code);
    }
  }
  return true;
}
